import { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Button, HStack, CheckboxGroup, Spinner, Text, Fieldset } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useController, useForm } from 'react-hook-form';
import { z } from 'zod';
import { toaster } from '@/components/ui/toaster';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import useContentTypesStore from '@/utils/use-content-type';
import { ContentType, WorkspaceContentType, DailogProps } from '@/lib/types';
import api from '@/utils/api';

interface Props extends DailogProps {
  workspaceId: number;
  sportTypeId: number;
  onSuccess: () => void;
}

const formSchema = z.object({
  contentTypes: z.array(z.string()).min(1, {
    message: 'You must select at least one content library.',
  }),
});

type FormData = z.infer<typeof formSchema>;

export default function CreateContentTypeModal({
  workspaceId,
  sportTypeId,
  isOpen,
  onClose,
  onSuccess,
}: Props) {
  const [types, setTypes] = useState<WorkspaceContentType[]>([]);
  const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
  const { loadContentTypes } = useContentTypesStore();
  const [loadingItems, setLoadingItems] = useState<Map<number, boolean>>(new Map());

  const previousSelectedRef = useRef<string[]>([]);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const controlType = useController({
    control,
    name: 'contentTypes',
    defaultValue: [],
  });

  const selectedContentTypes = watch('contentTypes');
  const invalid = !!errors.contentTypes;

  const onSubmit = async (data: FormData) => {
    const existingTypeIds = types.map((type) => type.content_type_id);
    const uniqueTypes = data.contentTypes.filter(
      (typeId) => !existingTypeIds.includes(parseInt(typeId, 10)),
    );

    if (uniqueTypes.length === 0) {
      onClose();
      return;
    }

    try {
      await api.post(`workspace-content-types/multiple`, {
        workspace_id: workspaceId,
        content_type_ids: uniqueTypes.map((typeId) => parseInt(typeId, 10)),
      });

      const resToken = await api.get(`workspace/${workspaceId}/token`);
      const tokenData = resToken.data.resource;
      if (tokenData) {
        localStorage.setItem('accessToken', tokenData);
        fetchContentTypes();
        loadContentTypes(workspaceId + '');
        onClose();
        onSuccess();

        toaster.create({
          type: 'success',
          title: 'Content libraries added successfully',
          duration: 3000,
        });
      }
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to create content library',
      });
    }
  };

  const fetchContentTypes = useCallback(async () => {
    try {
      const [baseTypes, workspaceResponse] = await Promise.all([
        api.get(`/content-types/base/${sportTypeId}`),
        api.get(`/workspace-content-types/${workspaceId}`),
      ]);

      const datas = [...(workspaceResponse.data.data as WorkspaceContentType[])];
      setContentTypes(baseTypes.data.data as ContentType[]);
      setTypes(datas);
      return datas.map((type) => type.content_type_id);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch content library',
      });
    }
  }, [sportTypeId, workspaceId]);

  useEffect(() => {
    if (isOpen) {
      reset();
      fetchContentTypes().then((existingTypes) => {
        controlType.field.onChange(existingTypes || []);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fetchContentTypes]);

  const handleDeleteContentType = useCallback(
    async (contentType: ContentType) => {
      if (loadingItems.get(contentType.id)) return;

      setLoadingItems((prev) => new Map(prev).set(contentType.id, true));

      try {
        const existingType = types.find((type) => type.content_type_id === contentType.id);

        if (existingType) {
          await api.delete(`workspace-content-types/${existingType.id}`);

          fetchContentTypes();

          if (workspaceId) {
            loadContentTypes(workspaceId + '');
          }

          toaster.create({
            title: `"${contentType.name}" library was removed`,
            type: 'success',
          });
        }
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to delete content library',
        });

        const currentValues = controlType.field.value || [];
        if (!currentValues.includes(contentType.id.toString())) {
          controlType.field.onChange([...currentValues, contentType.id.toString()]);
        }
      } finally {
        setLoadingItems((prev) => {
          const next = new Map(prev);
          next.delete(contentType.id);
          return next;
        });
      }
    },
    [loadingItems, types, fetchContentTypes, workspaceId, loadContentTypes, controlType.field],
  );

  useEffect(() => {
    if (!selectedContentTypes) return;

    const currentSelected = selectedContentTypes.map(String);
    const previousSelected = previousSelectedRef.current;

    const removedIds = previousSelected.filter((id) => !currentSelected.includes(id));

    if (removedIds.length > 0) {
      removedIds.forEach((removedId: any) => {
        const contentType = contentTypes.find((type) => type.id.toString() === removedId);
        if (contentType) {
          handleDeleteContentType(contentType);
        }
      });
    }

    previousSelectedRef.current = currentSelected;
  }, [selectedContentTypes, contentTypes, handleDeleteContentType]);

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={onClose}>
      <DialogBackdrop />
      <DialogTrigger />
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Add from library</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {contentTypes.length > 0 ? (
            <form id={'createContentType'} onSubmit={handleSubmit(onSubmit)}>
              <Fieldset.Root invalid={invalid}>
                <Fieldset.Legend>Select library</Fieldset.Legend>
                <CheckboxGroup
                  colorPalette='brand'
                  invalid={invalid}
                  name={controlType.field.name}
                  value={controlType.field.value?.map(String)}
                  onValueChange={controlType.field.onChange}
                >
                  <Fieldset.Content>
                    {contentTypes.map((contentType) => (
                      <Checkbox
                        key={contentType.id}
                        value={contentType.id.toString()}
                        disabled={loadingItems.get(contentType.id) || false}
                      >
                        <HStack>
                          <Text>{contentType.name}</Text>
                          {loadingItems.get(contentType.id) && <Spinner size='sm' />}
                        </HStack>
                      </Checkbox>
                    ))}
                  </Fieldset.Content>
                </CheckboxGroup>
                {errors.contentTypes && (
                  <Fieldset.ErrorText>{errors.contentTypes.message}</Fieldset.ErrorText>
                )}
              </Fieldset.Root>
            </form>
          ) : (
            <Box p={4} textAlign='center'>
              <Text color='fg.muted'>No content libraries available</Text>
            </Box>
          )}
        </DialogBody>
        <DialogFooter>
          <Button colorPalette='brand' variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorPalette='brand'
            form={'createContentType'}
            type={'submit'}
            loading={isSubmitting}
            loadingText='Loading...'
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
